import Fuse from 'fuse.js'
import { store } from 'src/store/configureStore'

export const performFuzzySearch = ({
  data,
  searchTerm,
  searchKey,
  outputKey,
  threshold = 0.1
}: {
  data: any[]
  searchTerm: string
  searchKey: string
  outputKey: string
  threshold?: number
}) => {
  if (!searchTerm) return null
  if (!data) return null

  const fuse = new Fuse(data, {
    keys: [searchKey],
    threshold: threshold
  })

  const result = fuse.search(searchTerm)

  return (result[0]?.item as any)?.[outputKey]
}

export const findUserIdFromUserName = (userName: string) => {
  const state = store.getState()
  const users = state.teams?.data

  return performFuzzySearch({
    data: users,
    searchTerm: userName,
    searchKey: 'user_name',
    outputKey: 'user_id'
  })
}

export const findProjectIdFromName = (projectName: string) => {
  const state = store.getState()
  const projects = state.projects.projectList

  return performFuzzySearch({
    data: projects,
    searchTerm: projectName,
    searchKey: 'name',
    outputKey: 'project_id'
  })
}

export const findWorkorderStage = (stage: string) => {
  const state = store.getState()
  const stages = state.displayNames.data?.stages

  return performFuzzySearch({
    data: Object.entries(stages).map(([key, value]) => ({ name: value, key })),
    searchTerm: stage,
    searchKey: 'name',
    outputKey: 'key'
  })
}

export const findWorkorderIdFromName = (workorderName: string) => {
  const state = store.getState()
  const workorders = state.workOrders.data

  return performFuzzySearch({
    data: workorders,
    searchTerm: workorderName,
    searchKey: 'work_order_name',
    outputKey: 'work_order_id'
  })
}

export const findVersionFromModel = (model: string) => {
  const state = store.getState()
  const models = state.formulate.configData

  return performFuzzySearch({
    data: models,
    searchTerm: model,
    searchKey: 'objective',
    outputKey: 'version'
  })
}

export const findLabLocationIdFromName = (labLocationName: string) => {
  const state = store.getState()
  const labLocations = state.settings.labsLocationList

  return performFuzzySearch({
    data: labLocations,
    searchTerm: labLocationName,
    searchKey: 'name',
    outputKey: 'lab_location_id'
  })
}

export const findIngredientIdFromName = (ingredientName: string) => {
  const state = store.getState()
  const ingredients = state.displayNames.data?.ingredients

  return performFuzzySearch({
    data: Object.values(ingredients).map(({name, identifier}:any) => ({ name, identifier })),
    searchTerm: ingredientName,
    searchKey: 'name',
    outputKey: 'identifier'
  })
}

export const findProcessingIdFromName = (processingName: string) => {
  const state = store.getState()
  const processings = state.displayNames.data?.processing

  return performFuzzySearch({
    data: Object.values(processings).map(({name, identifier}:any) => ({ name, identifier })),
    searchTerm: processingName,
    searchKey: 'name',
    outputKey: 'identifier'
  })
}

export const findPropertiesIdFromName = (propertiesName: string) => {
  const state = store.getState()
  const properties = state.displayNames.data?.properties
  return performFuzzySearch({
    data: Object.values(properties).map(({name, identifier}:any) => ({ name, identifier })),
    searchTerm: propertiesName,
    searchKey: 'name',
    outputKey: 'identifier'
  })
}

export const findCharacterizationIdFromName = (characterizationName: string) => {
  const state = store.getState()
  const characterizations = state.displayNames.data?.characterizations

  return performFuzzySearch({
    data: Object.values(characterizations).map(({name, identifier}:any) => ({ name, identifier })),
    searchTerm: characterizationName,
    searchKey: 'name',
    outputKey: 'identifier'
  })
}


export const findParameterCategoryFromId = (parameterId: string, parameterKey: string):string | null => {
  const state = store.getState()
  const parameters = state.displayNames.data?.[parameterKey]

  if (!parameters) return null
  const parameter = parameters[parameterId]

  return parameter?.category ? parameter?.category : null
}

export const isInternalEmail = (email:string) => {
  const email_lowercase = email.toLowerCase();
  return (
    email_lowercase.endsWith('@polymerize.io') ||
    email_lowercase.includes('labs.') ||
    email_lowercase.includes('test') ||
    email_lowercase.includes('demo')
  );
};